import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getSEOForRoute, getOrganizationSchema, getFAQSchema, getOrganizationKnowledgePanelSchema } from '../utils/seo';

/**
 * SEO Component
 * Manages route-specific meta tags, Open Graph, Twitter Cards, and structured data
 */
const SEO = ({ title, description, keywords, canonical, ogImage, noindex = false }) => {
  const location = useLocation();
  
  // Get route-specific SEO data if custom props not provided
  const routeSEO = getSEOForRoute(location.pathname);
  
  const seo = {
    title: title || routeSEO.title,
    description: description || routeSEO.description,
    keywords: keywords || routeSEO.keywords,
    canonical: canonical || routeSEO.canonical,
    ogTitle: routeSEO.ogTitle,
    ogDescription: routeSEO.ogDescription,
    ogImage: ogImage || routeSEO.ogImage,
    twitterImage: routeSEO.twitterImage,
  };

  const organizationSchema = getOrganizationSchema();
  const faqSchema = getFAQSchema();
  const organizationKnowledgePanelSchema = getOrganizationKnowledgePanelSchema();

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      
      {/* Canonical URL */}
      {seo.canonical && <link rel="canonical" href={seo.canonical} />}
      
      {/* Robots - Allow environment-based control */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={seo.ogTitle} />
      <meta property="og:description" content={seo.ogDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={seo.canonical} />
      <meta property="og:image" content={seo.ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Solidly Finance" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.ogTitle} />
      <meta name="twitter:description" content={seo.ogDescription} />
      <meta name="twitter:image" content={seo.twitterImage} />
      
      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationKnowledgePanelSchema)}
      </script>
    </Helmet>
  );
};

export default SEO;
